import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography
} from '@mui/material';

import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { MdOutlineEmail, MdOutlinePhone } from 'react-icons/md';
import SelectInput from '../components/Select';
import { FIELD_SIZE, Section, StickyActions } from '../components/Section';
import TextInput from '../components/TextInput';
import { ContactDetailsInitialValues, ContactDetailsSchemaValidation } from './Schema';
import {
  useDistrictsQuery,
  useProvincesQuery
} from 'redux/reducers/user/dashboard/tms-user-location';
import {
  useContactDetailsQuery,
  useCurrentUserDetailsQuery,
  useUpdateContactDetailMutation
} from 'redux/reducers/user/dashboard/tms-user';
import { toast } from 'react-toastify';

interface IOption {
  label: string;
  value: string;
}

const ContactDetailSection: React.FC<{
  title: string;
  description?: string;
  provinceId: string;
  children: (props: {
    changeProvince: (id: string) => void;
    districts: IOption[];
  }) => React.ReactNode;
  showCheckbox?: boolean;
  labelTitle?: string;
  onCheckboxChange?: (isChecked: boolean) => void;
}> = ({ title, description, children, provinceId, showCheckbox, onCheckboxChange, labelTitle }) => {
  useEffect(() => {
    changeProvince(provinceId);
  }, [provinceId]);

  const [currentProvince, setCurrentProvince] = useState(provinceId);
  const [districts, setDistricts] = useState<IOption[]>([]);
  useDistrictsQuery((data: any) => {
    const result: IOption[] = data?.map((district: any) => {
      return {
        label: district.name_en,
        value: district.id.toString()
      };
    });
    setDistricts(result);
  });

  const changeProvince = (id: string) => setCurrentProvince(id);

  return (
    <Section
      title={title}
      description={description}
      action={
        showCheckbox && (
          <FormControlLabel
            control={
              <Checkbox size="small" onChange={(e) => onCheckboxChange?.(e.target.checked)} />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                {labelTitle || ''}
              </Typography>
            }
            sx={{ mr: 0, ml: { xs: -1, sm: 0 } }}
          />
        )
      }
    >
      {children({ changeProvince, districts })}
    </Section>
  );
};

function ContactDetails({
  activeStep,
  setActiveStep
}: {
  activeStep: number;
  setActiveStep: (step: number) => void;
}) {
  const { data: provinces } = useProvincesQuery();

  const Provinces = provinces?.data?.provinces?.map((province: any) => {
    return {
      label: province.name_en,
      value: province.id.toString()
    };
  });

  const { data: CurrentUser, isLoading: isCurrentUserLoading } = useCurrentUserDetailsQuery();
  const TraineeId = CurrentUser?.id;

  const {
    data: contactDetails,
    isLoading: isContactDetailLoading,
    isSuccess
  } = useContactDetailsQuery(TraineeId ?? '', (data) => {
    formik.resetForm({
      values: {
        ...ContactDetailsInitialValues,
        mobile_number: data?.data?.mobile_number ?? '',
        email: data?.data?.email ?? '',
        // employee_id: data?.data?.employee_id ?? '',
        address: {
          province: data?.data?.address_data?.province_id ?? '',
          district: data?.data?.address_data?.district_id ?? '',
          municipality: data?.data?.address_data?.municipality ?? '',
          ward_no: data?.data?.address_data?.ward_no ?? '',
          house_num: data?.data?.address_data?.house_num ?? '',
          tole_street: data?.data?.address_data?.tole_street ?? '',
          block_no: data?.data?.address_data?.block_no ?? ''
        },
        birth_address: {
          province: data?.data?.birth_address_data?.province_id ?? '',
          district: data?.data?.birth_address_data?.district_id ?? '',
          municipality: data?.data?.birth_address_data?.municipality ?? '',
          ward_no: data?.data?.birth_address_data?.ward_no ?? '',
          house_num: data?.data?.birth_address_data?.house_num ?? '',
          tole_street: data?.data?.birth_address_data?.tole_street ?? '',
          block_no: data?.data?.birth_address_data?.block_no ?? ''
        },
        local_address: {
          province: data?.data?.local_address_data?.province_id ?? '',
          district: data?.data?.local_address_data?.district_id ?? '',
          municipality: data?.data?.local_address_data?.municipality ?? '',
          ward_no: data?.data?.local_address_data?.ward_no ?? '',
          house_num: data?.data?.local_address_data?.house_num ?? '',
          tole_street: data?.data?.local_address_data?.tole_street ?? '',
          block_no: data?.data?.local_address_data?.block_no ?? ''
        }
      }
    });
  });

  const { mutateAsync: updateContactDetail, isLoading: isUpdating } =
    useUpdateContactDetailMutation(TraineeId ?? '');

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: ContactDetailsSchemaValidation,
    initialValues: ContactDetailsInitialValues,
    onSubmit: async (values) => {
      await updateContactDetail(values)
        .then(() => {
          // setActiveStep(++activeStep);
          toast.success('Contact Details Successfully Updated.');
        })
        .catch((error: any) => {
          toast.error(error.response.data.message);
        });
    }
  });

  const isSaving = isUpdating || formik.isSubmitting;

  if (isCurrentUserLoading || isContactDetailLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 320 }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <Section title="Contact Information" description="How the academy reaches you.">
        <TextInput
          {...FIELD_SIZE}
          label="Mobile Number"
          name="mobile_number"
          type="text"
          startIcon={<MdOutlinePhone />}
          onChange={formik.handleChange}
          value={formik.values.mobile_number}
          onBlur={formik.handleBlur}
          formik={formik}
          required
        />
        <TextInput
          {...FIELD_SIZE}
          label="Email"
          name="email"
          type="text"
          startIcon={<MdOutlineEmail />}
          onChange={formik.handleChange}
          value={formik.values.email}
          onBlur={formik.handleBlur}
          formik={formik}
          required
        />
        {/* <TextInput
          label="Employee Id"
          name="employee_id"
          type="text"
          onChange={formik.handleChange}
          value={formik.values.employee_id}
          onBlur={formik.handleBlur}
          formik={formik}
          required
        /> */}
      </Section>

      <ContactDetailSection
        title="Birth Address"
        description="Place recorded on your birth registration."
        provinceId={contactDetails?.data?.birth_address_data?.province?.id ?? ''}
      >
        {({ changeProvince, districts }) => {
          return (
            <>
              <SelectInput
                {...FIELD_SIZE}
                label="Province"
                name="birth_address.province"
                options={Provinces || []}
                value={formik.values.birth_address.province.toString()}
                onChange={(e) => {
                  formik.handleChange(e);
                  changeProvince(e.target.value);
                }}
                required
              />
              <SelectInput
                {...FIELD_SIZE}
                label="District"
                name="birth_address.district"
                options={districts || []}
                value={formik.values.birth_address.district.toString()}
                onChange={(e) => {
                  formik.handleChange(e);
                }}
                required
              />
              <TextInput
                {...FIELD_SIZE}
                label="Municipality"
                name="birth_address.municipality"
                type="text"
                onChange={formik.handleChange}
                value={formik.values.birth_address.municipality}
                onBlur={formik.handleBlur}
                formik={formik}
                required
              />
              <TextInput
                {...FIELD_SIZE}
                label="Ward Number"
                name="birth_address.ward_no"
                type="number"
                onChange={formik.handleChange}
                value={formik.values.birth_address.ward_no}
                onBlur={formik.handleBlur}
                formik={formik}
              />
              <TextInput
                {...FIELD_SIZE}
                label="House Number"
                name="birth_address.house_num"
                type="text"
                onChange={formik.handleChange}
                value={formik.values.birth_address.house_num}
                onBlur={formik.handleBlur}
                formik={formik}
              />
              <TextInput
                {...FIELD_SIZE}
                label="Tole Street"
                name="birth_address.tole_street"
                type="text"
                onChange={formik.handleChange}
                value={formik.values.birth_address.tole_street}
                onBlur={formik.handleBlur}
                formik={formik}
              />
              <TextInput
                {...FIELD_SIZE}
                label="Block No."
                name="birth_address.block_no"
                type="text"
                onChange={formik.handleChange}
                value={formik.values.birth_address.block_no}
                onBlur={formik.handleBlur}
                formik={formik}
              />
            </>
          );
        }}
      </ContactDetailSection>

      <ContactDetailSection
        title="Address"
        description="Your permanent address."
        labelTitle="Same as Birth Address"
        provinceId={contactDetails?.data?.address_data?.province?.id ?? ''}
        showCheckbox={true}
        onCheckboxChange={(isChecked) => {
          if (isChecked) {
            formik.setFieldValue('address', formik.values.birth_address);
          } else {
            formik.setFieldValue('address', ContactDetailsInitialValues.address);
          }
        }}
      >
        {({ changeProvince, districts }) => {
          return (
            <>
              <SelectInput
                {...FIELD_SIZE}
                label="Province"
                name="address.province"
                options={Provinces || []}
                value={formik.values.address.province.toString()}
                onChange={(e) => {
                  formik.handleChange(e);
                  changeProvince(e.target.value);
                }}
                required
              />
              <SelectInput
                {...FIELD_SIZE}
                label="District"
                name="address.district"
                options={districts || []}
                value={formik.values.address.district.toString()}
                onChange={(e) => {
                  formik.handleChange(e);
                }}
                required
              />
              <TextInput
                {...FIELD_SIZE}
                label="Municipality"
                name="address.municipality"
                type="text"
                onChange={formik.handleChange}
                value={formik.values.address.municipality}
                onBlur={formik.handleBlur}
                formik={formik}
                required
              />
              <TextInput
                {...FIELD_SIZE}
                label="Ward Number"
                name="address.ward_no"
                type="number"
                onChange={formik.handleChange}
                value={formik.values.address.ward_no}
                onBlur={formik.handleBlur}
                formik={formik}
              />
              <TextInput
                {...FIELD_SIZE}
                label="House Number"
                name="address.house_num"
                type="text"
                onChange={formik.handleChange}
                value={formik.values.address.house_num}
                onBlur={formik.handleBlur}
                formik={formik}
              />
              <TextInput
                {...FIELD_SIZE}
                label="Tole Street"
                name="address.tole_street"
                type="text"
                onChange={formik.handleChange}
                value={formik.values.address.tole_street}
                onBlur={formik.handleBlur}
                formik={formik}
              />
              <TextInput
                {...FIELD_SIZE}
                label="Block No."
                name="address.block_no"
                type="text"
                onChange={formik.handleChange}
                value={formik.values.address.block_no}
                onBlur={formik.handleBlur}
                formik={formik}
              />
            </>
          );
        }}
      </ContactDetailSection>

      <ContactDetailSection
        title="Local Address"
        description="Where you currently live."
        labelTitle="Same as Above Address"
        provinceId={contactDetails?.data?.local_address_data?.province?.id ?? ''}
        showCheckbox={true}
        onCheckboxChange={(isChecked) => {
          if (isChecked) {
            formik.setFieldValue('local_address', formik.values.birth_address);
          } else {
            formik.setFieldValue('local_address', ContactDetailsInitialValues.local_address);
          }
        }}
      >
        {({ changeProvince, districts }) => {
          return (
            <>
              <SelectInput
                {...FIELD_SIZE}
                label="Province"
                name="local_address.province"
                options={Provinces || []}
                value={formik.values.local_address.province.toString()}
                onChange={(e) => {
                  formik.handleChange(e);
                  changeProvince(e.target.value);
                }}
                required
              />
              <SelectInput
                {...FIELD_SIZE}
                label="District"
                name="local_address.district"
                options={districts || []}
                value={formik.values.local_address.district.toString()}
                onChange={(e) => {
                  formik.handleChange(e);
                }}
                required
              />
              <TextInput
                {...FIELD_SIZE}
                label="Municipality"
                name="local_address.municipality"
                type="text"
                onChange={formik.handleChange}
                value={formik.values.local_address.municipality}
                onBlur={formik.handleBlur}
                formik={formik}
                required
              />
              <TextInput
                {...FIELD_SIZE}
                label="Ward Number"
                name="local_address.ward_no"
                type="number"
                onChange={formik.handleChange}
                value={formik.values.local_address.ward_no}
                onBlur={formik.handleBlur}
                formik={formik}
              />
              <TextInput
                {...FIELD_SIZE}
                label="House Number"
                name="local_address.house_num"
                type="text"
                onChange={formik.handleChange}
                value={formik.values.local_address.house_num}
                onBlur={formik.handleBlur}
                formik={formik}
              />
              <TextInput
                {...FIELD_SIZE}
                label="Tole Street"
                name="local_address.tole_street"
                type="text"
                onChange={formik.handleChange}
                value={formik.values.local_address.tole_street}
                onBlur={formik.handleBlur}
                formik={formik}
              />
              <TextInput
                {...FIELD_SIZE}
                label="Block No."
                name="local_address.block_no"
                type="text"
                onChange={formik.handleChange}
                value={formik.values.local_address.block_no}
                onBlur={formik.handleBlur}
                formik={formik}
              />
            </>
          );
        }}
      </ContactDetailSection>

      <StickyActions>
        {activeStep > 1 && (
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setActiveStep(--activeStep)}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Previous Step
          </Button>
        )}

        <Button
          type="submit"
          variant="contained"
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress color="inherit" size={16} /> : undefined}
          sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 120 }}
        >
          {isSaving ? 'Saving…' : 'Save'}
        </Button>

        <Button
          variant="outlined"
          onClick={() => {
            setActiveStep(++activeStep);
          }}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Next
        </Button>
      </StickyActions>
    </form>
  );
}

export default ContactDetails;
